import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Badge } from './components/ui/badge';
import GlobalFooter from './components/GlobalFooter';

const TransportNetwork = () => {
  const navigate = useNavigate();
  const [hoveredCity, setHoveredCity] = useState(null);

  // Dummy transport data for India
  const indiaTransport = [
    { id: 1, city: "Mumbai", status: "Active", vehicles: 12, radius: 25, position: { top: "65%", left: "15%" } },
    { id: 2, city: "Delhi", status: "Active", vehicles: 10, radius: 22, position: { top: "25%", left: "25%" } },
    { id: 3, city: "Chennai", status: "Limited", vehicles: 6, radius: 18, position: { top: "75%", left: "60%" } },
    { id: 4, city: "Hyderabad", status: "Active", vehicles: 9, radius: 20, position: { top: "60%", left: "50%" } },
    { id: 5, city: "Pune", status: "Active", vehicles: 7, radius: 15, position: { top: "62%", left: "20%" } },
    { id: 6, city: "Bangalore", status: "Active", vehicles: 11, radius: 25, position: { top: "70%", left: "45%" } },
    { id: 7, city: "Jaipur", status: "Limited", vehicles: 4, radius: 12, position: { top: "35%", left: "20%" } },
    { id: 8, city: "Kochi", status: "Active", vehicles: 5, radius: 18, position: { top: "80%", left: "40%" } },
    { id: 9, city: "Ahmedabad", status: "Limited", vehicles: 6, radius: 15, position: { top: "45%", left: "18%" } },
    { id: 10, city: "Lucknow", status: "Active", vehicles: 3, radius: 10, position: { top: "35%", left: "35%" } }
  ];

  // Dummy transport data for Singapore
  const singaporeTransport = [
    { id: 11, district: "Orchard", status: "Active", vehicles: 3, radius: 10, position: { top: "45%", left: "40%" } },
    { id: 12, district: "Marina Bay", status: "Active", vehicles: 2, radius: 8, position: { top: "55%", left: "45%" } },
    { id: 13, district: "Chinatown", status: "Active", vehicles: 2, radius: 6, position: { top: "60%", left: "40%" } },
    { id: 14, district: "Clarke Quay", status: "Limited", vehicles: 1, radius: 5, position: { top: "50%", left: "42%" } },
    { id: 15, district: "Tiong Bahru", status: "Active", vehicles: 2, radius: 7, position: { top: "65%", left: "38%" } },
    { id: 16, district: "Sentosa", status: "Limited", vehicles: 1, radius: 6, position: { top: "75%", left: "45%" } },
    { id: 17, district: "Bukit Timah", status: "Active", vehicles: 2, radius: 8, position: { top: "35%", left: "35%" } },
    { id: 18, district: "Jurong", status: "Active", vehicles: 3, radius: 9, position: { top: "55%", left: "25%" } },
    { id: 19, district: "Novena", status: "Limited", vehicles: 1, radius: 6, position: { top: "40%", left: "38%" } },
    { id: 20, district: "Katong", status: "Active", vehicles: 2, radius: 7, position: { top: "65%", left: "55%" } }
  ];

  // Calculate statistics
  const indiaStats = {
    activeCities: indiaTransport.filter(city => city.status === "Active").length,
    totalVehicles: indiaTransport.reduce((sum, city) => sum + city.vehicles, 0),
    avgRadius: Math.round(indiaTransport.reduce((sum, city) => sum + city.radius, 0) / indiaTransport.length)
  };

  const singaporeStats = {
    activeDistricts: singaporeTransport.filter(district => district.status === "Active").length,
    totalVehicles: singaporeTransport.reduce((sum, district) => sum + district.vehicles, 0),
    avgRadius: Math.round(singaporeTransport.reduce((sum, district) => sum + district.radius, 0) / singaporeTransport.length)
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active": return "bg-green-500";
      case "Limited": return "bg-yellow-500";
      default: return "bg-red-500";
    }
  };

  const MapTooltip = ({ city, onClose }) => (
    <div className="absolute bg-white border border-gray-200 rounded-lg shadow-xl p-4 z-50 min-w-[200px]">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-gray-900">{city.city || city.district}</h4>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
      </div>
      <div className="space-y-2 text-sm">
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-full ${getStatusColor(city.status)}`}></div>
          <span className="font-medium">{city.status} Coverage</span>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-2xl">🚚</span>
          <span>{city.vehicles} Delivery Vehicles</span>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-2xl">📍</span>
          <span>{city.radius} km Coverage Radius</span>
        </div>
      </div>
    </div>
  );

  const IndiaMap = ({ data }) => (
    <div className="relative bg-gradient-to-br from-blue-50 to-green-50 rounded-xl p-4 h-96 overflow-hidden border-2 border-gray-200">
      {/* India Map SVG Outline */}
      <svg viewBox="0 0 400 500" className="absolute inset-0 w-full h-full opacity-20">
        <path
          d="M150 50 L180 45 L200 55 L230 65 L250 80 L270 100 L280 130 L285 160 L280 190 L270 220 L260 250 L240 280 L220 310 L200 340 L180 360 L160 380 L140 390 L120 385 L100 375 L85 360 L75 340 L70 320 L75 300 L85 280 L100 260 L115 240 L125 220 L130 200 L135 180 L140 160 L145 140 L148 120 L150 100 L150 80 L150 60 Z"
          fill="currentColor"
          className="text-green-200"
          stroke="currentColor"
          strokeWidth="1"
        />
      </svg>
      
      {/* Coverage Areas */}
      {data.map((location) => (
        <div
          key={location.id}
          className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
          style={{ top: location.position.top, left: location.position.left }}
          onMouseEnter={() => setHoveredCity(location)}
          onMouseLeave={() => setHoveredCity(null)}
        >
          {/* Coverage Circle */}
          <div 
            className={`absolute w-16 h-16 rounded-full opacity-30 animate-pulse`}
            style={{ 
              backgroundColor: location.status === 'Active' ? '#22c55e' : '#eab308',
              transform: 'translate(-50%, -50%)',
              top: '50%',
              left: '50%'
            }}
          ></div>
          
          {/* City Marker */}
          <div className={`w-8 h-8 ${getStatusColor(location.status)} rounded-full border-2 border-white shadow-lg flex items-center justify-center hover:scale-110 transition-transform`}>
            <span className="text-white text-xs font-bold">{location.vehicles}</span>
          </div>
          
          {/* City Label */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 text-xs font-medium text-gray-700 bg-white px-2 py-1 rounded shadow-sm whitespace-nowrap">
            {location.city}
          </div>
        </div>
      ))}

      {/* Tooltip */}
      {hoveredCity && (
        <div 
          className="absolute z-50"
          style={{ 
            top: `calc(${hoveredCity.position.top} - 140px)`, 
            left: `calc(${hoveredCity.position.left} + 60px)` 
          }}
        >
          <MapTooltip city={hoveredCity} onClose={() => setHoveredCity(null)} />
        </div>
      )}

      {/* Map Title Overlay */}
      <div className="absolute bottom-4 left-4 bg-white bg-opacity-90 px-3 py-2 rounded-lg shadow-sm">
        <p className="text-sm font-medium text-gray-700">🇮🇳 India Transport Network</p>
        <p className="text-xs text-gray-500">Hover markers for details</p>
      </div>
    </div>
  );

  const SingaporeMap = ({ data }) => (
    <div className="relative bg-gradient-to-br from-red-50 to-blue-50 rounded-xl p-4 h-96 overflow-hidden border-2 border-gray-200">
      {/* Singapore Map SVG Outline */}
      <svg viewBox="0 0 400 200" className="absolute inset-0 w-full h-full opacity-20">
        <path
          d="M80 80 L320 80 L320 85 L315 90 L310 95 L305 100 L300 105 L290 110 L280 112 L270 115 L260 117 L250 118 L240 118 L230 118 L220 117 L210 115 L200 113 L190 110 L180 107 L170 104 L160 101 L150 98 L140 95 L130 92 L120 89 L110 86 L100 83 L90 81 L80 80 Z"
          fill="currentColor"
          className="text-red-200"
          stroke="currentColor"
          strokeWidth="1"
        />
      </svg>
      
      {/* Coverage Areas */}
      {data.map((location) => (
        <div
          key={location.id}
          className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
          style={{ top: location.position.top, left: location.position.left }}
          onMouseEnter={() => setHoveredCity(location)}
          onMouseLeave={() => setHoveredCity(null)}
        >
          {/* Coverage Circle */}
          <div 
            className={`absolute w-12 h-12 rounded-full opacity-30 animate-pulse`}
            style={{ 
              backgroundColor: location.status === 'Active' ? '#22c55e' : '#eab308',
              transform: 'translate(-50%, -50%)',
              top: '50%',
              left: '50%'
            }}
          ></div>
          
          {/* District Marker */}
          <div className={`w-7 h-7 ${getStatusColor(location.status)} rounded-full border-2 border-white shadow-lg flex items-center justify-center hover:scale-110 transition-transform`}>
            <span className="text-white text-xs font-bold">{location.vehicles}</span>
          </div>
          
          {/* District Label */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 text-xs font-medium text-gray-700 bg-white px-2 py-1 rounded shadow-sm whitespace-nowrap">
            {location.district}
          </div>
        </div>
      ))}

      {/* Tooltip */}
      {hoveredCity && (
        <div 
          className="absolute z-50"
          style={{ 
            top: `calc(${hoveredCity.position.top} - 140px)`, 
            left: `calc(${hoveredCity.position.left} + 60px)` 
          }}
        >
          <MapTooltip city={hoveredCity} onClose={() => setHoveredCity(null)} />
        </div>
      )}

      {/* Map Title Overlay */}
      <div className="absolute bottom-4 left-4 bg-white bg-opacity-90 px-3 py-2 rounded-lg shadow-sm">
        <p className="text-sm font-medium text-gray-700">🇸🇬 Singapore Transport Network</p>
        <p className="text-xs text-gray-500">Hover markers for details</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <section className="py-16 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="container mx-auto px-4 max-w-6xl text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Our Transport <span className="text-green-600">Network</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Real-time coverage of Food Connect's delivery and logistics partners. 
            Connecting surplus food to communities with reliable, sustainable transport.
          </p>
        </div>
      </section>

      {/* Coverage Maps */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <Tabs defaultValue="india" className="w-full">
            <div className="flex justify-center mb-12">
              <TabsList className="grid w-full max-w-md grid-cols-2">
                <TabsTrigger value="india" className="text-lg py-3" data-testid="india-tab">
                  🇮🇳 India Coverage
                </TabsTrigger>
                <TabsTrigger value="singapore" className="text-lg py-3" data-testid="singapore-tab">
                  🇸🇬 Singapore Coverage
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="india" className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">India Transport Coverage</h2>
                <div className="flex justify-center space-x-8 text-sm text-gray-600 mb-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span>Active Coverage</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <span>Limited Coverage</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span>No Coverage</span>
                  </div>
                </div>
              </div>
              
              <Card className="border-0 shadow-xl">
                <CardHeader>
                  <CardTitle className="text-center">Interactive Coverage Map - India</CardTitle>
                  <CardDescription className="text-center">
                    Hover over cities to see detailed transport information
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <IndiaMap data={indiaTransport} />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="singapore" className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Singapore Transport Coverage</h2>
                <div className="flex justify-center space-x-8 text-sm text-gray-600 mb-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span>Active Coverage</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <span>Limited Coverage</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span>No Coverage</span>
                  </div>
                </div>
              </div>
              
              <Card className="border-0 shadow-xl">
                <CardHeader>
                  <CardTitle className="text-center">Interactive Coverage Map - Singapore</CardTitle>
                  <CardDescription className="text-center">
                    Hover over districts to see detailed transport information
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <SingaporeMap data={singaporeTransport} />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Sections moved to Volunteer Network page */}

      {/* Real-time Updates Section and CTA Section moved to Volunteer Network page */}

      {/* Global Footer */}
      <GlobalFooter />
    </div>
  );
};

export default TransportNetwork;